import { useTranslation } from 'react-i18next';
import type { FlattenedBetslipCustomLeg } from '@/domain/betslip';
import { cn } from '@/lib/cn';
import { Icon } from '../common/icon';

export interface BetslipCustomPackageRowProps {
  match: string;
  odds: string;
  legs: FlattenedBetslipCustomLeg[];
  resolveMarketLabel: (marketKey: string) => string;
  onRemove?: () => void;
  onRemoveLeg?: (legKey: string) => void;
  className?: string;
}

type ConnectorPosition = 'first' | 'middle' | 'last' | 'only';

/**
 * Figma BetslipBetBuilderGroup connector — fixed 44px segment so LineTop + Dot +
 * LineBottom abut across legs (17 + 10 + 17) with no gaps.
 */
function LegConnector({ position }: { position: ConnectorPosition }) {
  const showTop = position === 'middle' || position === 'last';
  const showBottom = position === 'first' || position === 'middle';

  return (
    <div className="relative flex h-11 w-4 shrink-0 flex-col items-center justify-center">
      <span
        aria-hidden
        className={cn('h-[17px] w-0.5 shrink-0', showTop ? 'bg-action-primary' : 'bg-transparent')}
      />
      <span
        aria-hidden
        className="size-2.5 shrink-0 rounded-full border-[1.5px] border-action-primary bg-bg-surface"
      />
      <span
        aria-hidden
        className={cn(
          'h-[17px] w-0.5 shrink-0',
          showBottom ? 'bg-action-primary' : 'bg-transparent',
        )}
      />
    </div>
  );
}

/**
 * Figma BetslipBetBuilderGroup — left remove column, BB badge, odds pill, nested
 * legs on a continuous action-primary connector rail.
 */
export function BetslipCustomPackageRow({
  match,
  odds,
  legs,
  resolveMarketLabel,
  onRemove,
  onRemoveLeg,
  className,
}: BetslipCustomPackageRowProps) {
  const { t } = useTranslation();
  const bbLabel = t('BB', { defaultValue: 'BB' });

  return (
    <article
      className={cn(
        'flex shrink-0 items-stretch overflow-hidden rounded-md bg-bg-surface',
        className,
      )}
    >
      <button
        type="button"
        onClick={onRemove}
        aria-label={t('Remove', { defaultValue: 'Remove' })}
        className="flex w-11 shrink-0 items-center justify-center bg-bg-elevated text-icon-muted transition-colors hover:text-text-primary"
      >
        <Icon name="close" size={16} />
      </button>

      <div className="flex min-w-0 flex-1 flex-col gap-2.5 p-3">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className="flex h-[18px] w-[22px] shrink-0 items-center justify-center rounded-[3px] bg-action-primary text-[9px] font-bold leading-none text-text-on-action"
            aria-label={t('BetBuilder', { defaultValue: 'Bet Builder' })}
          >
            {bbLabel}
          </span>
          <p className="min-w-0 flex-1 truncate text-[13px] font-bold text-text-primary">{match}</p>
          <span className="shrink-0 rounded-sm bg-bg-elevated px-2 py-1 text-[12px] font-bold tabular-nums text-text-primary">
            {odds}
          </span>
        </div>

        {legs.length > 0 ? (
          <ul className="flex w-full flex-col">
            {legs.map((leg, index) => {
              const position: ConnectorPosition =
                legs.length === 1
                  ? 'only'
                  : index === 0
                    ? 'first'
                    : index === legs.length - 1
                      ? 'last'
                      : 'middle';
              return (
                <li key={leg.key} className="flex h-11 w-full items-center gap-2">
                  <LegConnector position={position} />
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <p className="truncate text-[14px] font-bold leading-normal text-text-primary">
                      {leg.outcomeName}
                    </p>
                    <p className="truncate text-[12px] leading-normal text-text-secondary">
                      {leg.marketLabel ? resolveMarketLabel(leg.marketLabel) : ''}
                    </p>
                  </div>
                  {onRemoveLeg ? (
                    <button
                      type="button"
                      onClick={() => onRemoveLeg(leg.key)}
                      aria-label={t('Remove', { defaultValue: 'Remove' })}
                      className="flex size-4 shrink-0 items-center justify-center text-icon-muted transition-colors hover:text-text-primary"
                    >
                      <Icon name="close" size={16} />
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </article>
  );
}
